


















































































































































































import { Component, Prop, Watch } from 'vue-property-decorator';
import { mixins } from 'vue-class-component';
import {
  EditedImage,
  EditedImages,
  EditedImagesDTO,
  ImageStatus,
  JbicImage,
  JbicImageVersion
} from '@/store/modules/admin/types/admin.types';
import { PaginationMixin } from '@/components/base/pagination.mixin';
import BaseTable from '@/components/BaseTable.vue';
import SortableTableHeader from '@/components/SortableTableHeader.vue';
import TableActionDropdown from '@/components/TableActionDropdown.vue';
import dayjs from 'dayjs';
import { checkJbicPermission } from '@/utils/rbac.util';
import { JbicPermissions } from '@/store/modules/roles-and-permissions/types/roles-and-permissions.types';
import { Action, State } from 'vuex-class';
import { RootState } from '@/store/store';
import { ApiState } from '@/store/types/general.types';

@Component({
  components: {
    BaseTable,
    SortableTableHeader,
    TableActionDropdown
  }
})
export default class ImagesList extends mixins(PaginationMixin) {
  @Prop() public items!: JbicImage[];
  @Prop(Number) public currentPage!: number;
  @Prop(Number) public totalNumberOfPage!: number;
  @Prop(Number) public totalCount!: number;
  @Prop(Boolean) public isFirstPage!: boolean;
  @Prop(Boolean) public isLastPage!: boolean;
  @Prop(Number) public startItemIndex!: number;
  @Prop(Number) public endItemIndex!: number;
  @Prop() public imageStatus!: ImageStatus;
  @Prop(Boolean) public isSelectAll!: boolean;
  @Prop(Boolean) public isDeselectAll!: boolean;

  @Action('admin/getEditedImages')
  getEditedImages!: (imageIds?: EditedImagesDTO) => void;

  @State(({ admin }: RootState) => admin.apiState.getEditedImages)
  getEditedImagesApiState!: ApiState;

  @State(({ admin }: RootState) => admin.editedImages)
  editedImages!: EditedImages;

  public selectedItems: number[] = [];
  public sortHeaders = [
    { placeholder: 'Media', sort: false },
    { placeholder: 'Caption', sort: false },
    { placeholder: 'Alternate Text', sort: false },
    { placeholder: `${this.updatedLabelByStatus} On`, sort: false },
    { placeholder: `${this.updatedLabelByStatus} By`, sort: false }
  ];
  public previousVersionImages: Record<number, JbicImageVersion> = {};

  get updatedLabelByStatus() {
    if (this.imageStatus === ImageStatus.DRAFT) {
      return 'Uploaded';
    } else if (this.imageStatus === ImageStatus.APPROVED) {
      return 'Approved';
    } else if (this.imageStatus === ImageStatus.DECLINED) {
      return 'Denied';
    } else if (this.imageStatus === ImageStatus.PUBLISHED) {
      return 'Published';
    } else {
      return 'Updated';
    }
  }

  get imageIds(): number[] {
    return this.items.map((item) => item.id);
  }

  get groupId(): number {
    return this.items[0].groupId;
  }

  get isJbicImageCreateRole(): boolean {
    return checkJbicPermission(JbicPermissions.IMAGE_CREATE, this.groupId);
  }

  get isJbicImageUpdateRole(): boolean {
    return checkJbicPermission(JbicPermissions.IMAGE_UPDATE, this.groupId);
  }

  get isJbicImageDeleteRole(): boolean {
    return checkJbicPermission(JbicPermissions.IMAGE_DELETE, this.groupId);
  }

  get isJbicImageViewRole(): boolean {
    return checkJbicPermission(JbicPermissions.IMAGE_READ, this.groupId);
  }

  get isJbicImageManageRole(): boolean {
    return checkJbicPermission(JbicPermissions.IMAGE_MANAGE, this.groupId);
  }

  get isActionable(): boolean {
    return !(
      this.isJbicImageManageRole &&
      [ImageStatus.DECLINED, ImageStatus.APPROVED].includes(this.imageStatus)
    );
  }

  isImageStatus(status: ImageStatus): boolean {
    return this.imageStatus === status;
  }

  mounted() {
    const imageIds: number[] = this.items.map((item) => item.id);
    this.getEditedImages({ ids: imageIds });
  }

  @Watch('selectedItems')
  onSelectedItems() {
    this.$emit('selectImages', this.selectedItems);
  }

  @Watch('isSelectAll')
  onIsSelectAll() {
    const isSelectAll: boolean = this.isSelectAll;

    if (isSelectAll) {
      this.selectedItems = [];
      this.imageIds.forEach((imageId) => {
        this.selectedItems.push(imageId);
      });
    }
  }

  @Watch('isDeselectAll')
  onIsDeselectAll() {
    const isDeselectAll: boolean = this.isDeselectAll;

    if (isDeselectAll) {
      this.selectedItems = [];
    }
  }

  updateImageStatusSubmission(imageId: number): void {
    if (!this.isJbicImageUpdateRole) {
      return;
    }

    this.$parent?.$parent?.$emit('updateImageStatusSubmission', [imageId]);
  }

  approveImage(imageId: number): void {
    if (!this.isJbicImageManageRole) {
      return;
    }

    this.$parent?.$parent?.$emit('approveImage', [imageId]);
  }

  declineImage(imageId: number): void {
    if (!this.isJbicImageManageRole) {
      return;
    }

    this.$parent?.$parent?.$emit('declineImage', [imageId]);
  }

  publishImage(imageId: number): void {
    if (!this.isJbicImageUpdateRole) {
      return;
    }

    this.$parent?.$parent?.$emit('publishImage', [imageId]);
  }

  unpublishImage(imageId: number): void {
    if (!this.isJbicImageUpdateRole) {
      return;
    }

    this.$parent?.$parent?.$emit('unpublishImage', [imageId]);
  }

  deleteImage(imageId: number): void {
    if (!this.isJbicImageDeleteRole) {
      return;
    }

    this.$parent?.$parent?.$emit('deleteImage', [imageId]);
  }

  editImage(image: JbicImage) {
    const imageVersion: JbicImageVersion | undefined = this.getPreviousVersion(
      image.id
    );
    const editedImage: EditedImage = {
      images: image,
      lastReviewImages: imageVersion ?? null
    };
    this.$parent?.$parent?.$emit('toggleEditImage', editedImage);
  }

  goToDetails(image: JbicImage) {
    const imageVersion: JbicImageVersion | undefined = this.getPreviousVersion(
      image.id
    );
    const editedImage: EditedImage = {
      images: image,
      lastReviewImages: imageVersion ?? null
    };
    this.$parent?.$parent?.$emit('toggleViewImage', editedImage);
  }

  formatDate(date: Date) {
    if (!date) {
      return '';
    }

    return dayjs(date).format('D MMM YYYY, h:mm A');
  }

  @Watch('imageStatus')
  onStatusChange() {
    this.selectedItems = [];
    this.sortHeaders = [
      { placeholder: 'Media', sort: false },
      { placeholder: 'Alternate Text', sort: false },
      { placeholder: 'Caption', sort: false },
      { placeholder: `${this.updatedLabelByStatus} On`, sort: false },
      { placeholder: `${this.updatedLabelByStatus} By`, sort: false }
    ];
  }

  @Watch('getEditedImagesApiState')
  onEditedImages() {
    if (
      this.getEditedImagesApiState.loading ||
      !this.getEditedImagesApiState.success ||
      this.getEditedImagesApiState.error
    ) {
      return;
    }

    const lastReviewImages = this.editedImages.lastReviewImages;

    if (!lastReviewImages) {
      return;
    }

    this.previousVersionImages = {};
    lastReviewImages.forEach((lastReviewImage) => {
      this.previousVersionImages[lastReviewImage.imageId] = lastReviewImage;
    });
  }

  getPreviousVersion(imageId: number): JbicImageVersion | undefined {
    return this.previousVersionImages[imageId];
  }

  hasPreviousVersion(imageId: number): boolean {
    return imageId in this.previousVersionImages;
  }

  showPreviousVersion(previousValue: string, currentValue: string): boolean {
    if (!previousValue || previousValue.length === 0) {
      return false;
    }

    if (!currentValue || currentValue.length === 0) {
      return false;
    }

    if (previousValue === currentValue) {
      return false;
    }

    return true;
  }
}
